"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, TrendingUp, Coins, ChartLine as LineChart, HeartHandshake, Umbrella, ScrollText, GraduationCap, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const services = [{
  id: "life-insurance",
  title: "Life insurance",
  description: "Permanent and term life options structured for long-term family stability and tax-advantaged cash value growth.",
  icon: ShieldCheck,
  target: "/FAQKnowledgeCenterPage#life-insurance"
}, {
  id: "fixed-annuities",
  title: "Fixed & fixed indexed annuities",
  description: "Principal-protected accumulation linked to market performance with guaranteed lifetime income options.",
  icon: TrendingUp,
  target: "/FAQKnowledgeCenterPage#fixed-fixed-indexed-annuities"
}, {
  id: "myga-annuities",
  title: "Multi-year guarantee annuities (MYGA)",
  description: "Reliable fixed-yield compounding with locked-in interest rates for predictable, conservative growth.",
  icon: Coins,
  target: "/FAQKnowledgeCenterPage#mygas"
}, {
  id: "retirement-income",
  title: "Retirement income planning",
  description: "Structured withdrawal baselines engineered to mitigate sequence risk and maintain steady cash flow.",
  icon: LineChart,
  target: "/FAQKnowledgeCenterPage#retirement-income"
}, {
  id: "final-expense",
  title: "Final expense insurance",
  description: "Streamlined coverage designed to lift immediate burial, administrative, and healthcare costs from loved ones.",
  icon: HeartHandshake,
  target: "/FAQKnowledgeCenterPage#final-expense"
}, {
  id: "income-protection",
  title: "Income protection",
  description: "Resilient disability and illness safety nets ensuring your household stays solvent when you cannot work.",
  icon: Umbrella,
  target: "/FAQKnowledgeCenterPage#income-protection"
}, {
  id: "legacy-planning",
  title: "Legacy planning",
  description: "Thoughtful transfer frameworks facilitating smooth, tax-efficient intergenerational wealth distribution.",
  icon: ScrollText,
  target: "/FAQKnowledgeCenterPage#legacy-planning"
}, {
  id: "financial-education",
  title: "Financial education",
  description: "Straightforward, jargon-free consultations that empower you to understand your options before making decisions.",
  icon: GraduationCap,
  target: "/FAQKnowledgeCenterPage"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 14
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="services-overview" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 bg-muted text-foreground border-border px-3.5 py-1 text-xs font-semibold tracking-wider uppercase rounded-full">
            Areas of Focus
          </Badge>

          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-foreground leading-[1.2] tracking-tight mb-5">
            Clear guidance for life, retirement, and legacy
          </h2>

          {/* Architectural Anchor Dot Rule */}
          <div className="flex items-center justify-center gap-2 w-full max-w-xs my-1 mb-6">
            <div className="h-[1px] flex-1 bg-border" />
            <div className="size-1.5 rounded-full bg-primary" />
            <div className="h-[1px] flex-1 bg-border" />
          </div>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
            We make insurance and retirement-income concepts easier to understand, so you can make informed decisions based on your needs and goals.
          </p>
        </motion.div>

        {/* 8-Card Responsive Grid */}
        <motion.div variants={shouldReduceMotion ? {} : containerVariants} initial={shouldReduceMotion ? "show" : "hidden"} whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} data-index={i} variants={shouldReduceMotion ? {} : itemVariants} className="flex">
                <Card className="group relative w-full flex flex-col justify-between rounded-xl bg-card border border-border p-6 shadow-xs hover:shadow-md hover:-translate-y-1 hover:border-primary/40 transition-[box-shadow,border-color,transform] duration-300">
                  <div>
                    <CardHeader className="p-0 mb-5 flex flex-row items-center justify-between gap-4">
                      <div className="size-12 rounded-lg bg-muted flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <IconComponent className="size-6 stroke-[1.75]" />
                      </div>
                      <span className="text-xs font-mono font-medium text-muted-foreground/60">
                        0{i + 1}
                      </span>
                    </CardHeader>

                    <CardTitle className="text-lg font-semibold text-card-foreground leading-snug mb-2.5">
                      {service.title}
                    </CardTitle>

                    <CardContent className="p-0 text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardContent>
                  </div>

                  <Link to={service.target} className="mt-6 pt-4 border-t border-border/60 flex items-center text-xs font-semibold text-foreground group-hover:text-primary transition-colors duration-200">
                    <span className="inline-flex items-center gap-1.5">
                      Explore options
                      <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Educational Callout */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-14 sm:mt-16 bg-muted/70 border border-border rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center text-center">
          <div className="max-w-2xl">
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">
              Have questions about which strategy fits your stage in life?
            </h3>
            <p className="text-sm text-muted-foreground">
              Schedule a relaxed conversation to talk through your questions at your own pace.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}